import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo } from 'react-icons/fa';
import { LuMenu } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'portal-header';

const defaultForm = () => ({
  isPublished: true,
  welcomePrefix: 'Hello,',
  welcomeSubtitle: 'Welcome back to your dashboard',
  notificationsTooltip: 'Notifications',
  notificationsTitle: 'Notifications',
  notificationsEmptyText: 'No new notifications',
  markAllReadText: 'Mark all read',
  chatButtonText: 'Chat with Docs',
  chatButtonIcon: 'FaComments',
  profileMenuText: 'View Profile',
  profileMenuIcon: 'FaUser',
  logoutText: 'Logout',
});

const HeaderPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setForm({ ...defaultForm(), ...(res.data.data.content || {}), isPublished: res.data.data.isPublished !== false });
      }
    } catch {
      toastError('Failed to load header content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Header saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded header defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Header"
        description="Edit welcome message, notification labels, chat button, and profile menu text."
        icon={LuMenu}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms/sidebar-header')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))} />
          Published
        </label>

        <h3 className="font-bold pt-2">Welcome message</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.welcomePrefix} onChange={(e) => setForm((prev) => ({ ...prev, welcomePrefix: e.target.value }))} placeholder="Prefix (e.g. Hello,)" />
          <input className="input-modern" value={form.welcomeSubtitle} onChange={(e) => setForm((prev) => ({ ...prev, welcomeSubtitle: e.target.value }))} placeholder="Subtitle below name" />
        </div>

        <h3 className="font-bold pt-2">Notifications</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.notificationsTooltip} onChange={(e) => setForm((prev) => ({ ...prev, notificationsTooltip: e.target.value }))} placeholder="Bell tooltip" />
          <input className="input-modern" value={form.notificationsTitle} onChange={(e) => setForm((prev) => ({ ...prev, notificationsTitle: e.target.value }))} placeholder="Dropdown title" />
          <input className="input-modern" value={form.notificationsEmptyText} onChange={(e) => setForm((prev) => ({ ...prev, notificationsEmptyText: e.target.value }))} placeholder="Empty state text" />
          <input className="input-modern" value={form.markAllReadText} onChange={(e) => setForm((prev) => ({ ...prev, markAllReadText: e.target.value }))} placeholder="Mark all read button" />
        </div>

        <h3 className="font-bold pt-2">Chat button</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.chatButtonText} onChange={(e) => setForm((prev) => ({ ...prev, chatButtonText: e.target.value }))} placeholder="Button text" />
          <PortalIconPicker value={form.chatButtonIcon} onChange={(icon) => setForm((prev) => ({ ...prev, chatButtonIcon: icon }))} />
        </div>

        <h3 className="font-bold pt-2">Profile menu</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.profileMenuText} onChange={(e) => setForm((prev) => ({ ...prev, profileMenuText: e.target.value }))} placeholder="View profile text" />
          <PortalIconPicker value={form.profileMenuIcon} onChange={(icon) => setForm((prev) => ({ ...prev, profileMenuIcon: icon }))} />
          <input className="input-modern" value={form.logoutText} onChange={(e) => setForm((prev) => ({ ...prev, logoutText: e.target.value }))} placeholder="Logout text" />
        </div>
      </div>
    </PageLayout>
  );
};

export default HeaderPortalCMS;
